import React, { useCallback, useState, useRef, useEffect, type UIEvent } from 'react';
import { User } from 'lucide-react';
import { XCircle } from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';
import { optimizeImage } from '@/lib/imageOptimization';
import { useIsMobile } from '@/hooks/use-mobile';
import linkedinIcon from '@/assets/linkedin-icon-white.svg';
import linkedinIconSquare from '@/assets/linkedin-icon-square.svg';
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';

export interface PeopleProfileExperience {
  id: string;
  experience: string;
}

export interface PeopleProfileCredential {
  id: string;
  credential: string;
}

export interface PeopleProfileEducation {
  id: string;
  education: string;
}

export interface PeopleProfileSpecialisation {
  id: string;
  specialisation: string;
}

export interface PeopleProfileData {
  id: string;
  name: string;
  title: string;
  bio: string;
  byline?: string | null;
  image_url: string | null;
  linkedin_url: string | null;
  experiences?: PeopleProfileExperience[];
  credentials?: PeopleProfileCredential[];
  education?: PeopleProfileEducation[];
  specialisations?: PeopleProfileSpecialisation[];
  years_experience?: number | null;
}

interface PeopleProfileDialogProps {
  profile: PeopleProfileData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/* ── Shared sub-sections ─────────────────────────────────────────── */

function YearsSubtitle({ years, className }: { years?: number | null; className?: string }) {
  if (!years || years <= 0) return null;
  return (
    <span className={cn('text-14 text-coal block', className)}>
      {years}+ years of experience
    </span>
  );
}

function CredentialsSection({ items, textSize = 'text-[14px]' }: { items?: PeopleProfileCredential[]; textSize?: string }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <span className="text-17 font-medium text-graphite block mb-3">Credentials</span>
      <div className="flex flex-col gap-2">
        {items.map(c => (
          <p key={c.id} className={cn(textSize, 'font-normal text-carbon leading-tight')}>
            {c.credential}
          </p>
        ))}
      </div>
    </div>
  );
}

function EducationSection({ items, textSize = 'text-[12px]' }: { items?: PeopleProfileEducation[]; textSize?: string }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <span className="text-17 font-medium text-graphite block mb-3">Education</span>
      <div className="flex flex-col gap-2">
        {items.map(e => (
          <p key={e.id} className={cn(textSize, 'font-normal text-carbon leading-tight')}>
            {e.education}
          </p>
        ))}
      </div>
    </div>
  );
}

function SpecialisationsSection({ items, label = 'Specialisations' }: { items?: PeopleProfileSpecialisation[]; label?: string }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <span className="text-17 font-medium text-graphite block mb-3">{label}</span>
      <div className="flex flex-wrap gap-2">
        {items.map(s => (
          <span
            key={s.id}
             className="text-[11px] font-medium text-coal bg-grey rounded-full px-3 py-1.5"
          >
            {s.specialisation}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Main dialog ─────────────────────────────────────────────────── */

/**
 * component-dialog-people
 *
 * Full-screen takeover dialog for people profiles.
 * Desktop: 3-column grid (Image | Bio+Credentials+Education | Experience+Specialisations)
 * Mobile: Scrolling card with pinned close button and floating author pill
 */
export function PeopleProfileDialog({
  profile,
  open,
  onOpenChange,
}: PeopleProfileDialogProps) {
  const [isMobileCloseOnDark, setIsMobileCloseOnDark] = useState(true);
  const [showFloatingAuthor, setShowFloatingAuthor] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // ⚠️ ANALYTICS: Track modal open time
  const modalOpenTime = useRef<number>(0);
  // ⚠️ ANALYTICS: Content viewed tracking refs
  const contentViewedRef = useRef(false);
  const contentViewedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset scroll and state when dialog opens + preload profile image
  useEffect(() => {
    if (open && profile?.image_url) {
      const mobileUrl = optimizeImage(profile.image_url, { width: 750 });
      const desktopUrl = optimizeImage(profile.image_url, { width: 900 });
      const preloadUrl = isMobile ? mobileUrl : desktopUrl;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = preloadUrl;
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(link);
      };
    }
  }, [open, profile?.image_url, isMobile]);

  useEffect(() => {
    if (open) {
      setIsMobileCloseOnDark(true);
      setShowFloatingAuthor(false);
      // ⚠️ ANALYTICS: Track modal open time
      modalOpenTime.current = Date.now();
      contentViewedRef.current = false;

      // ⚠️ ANALYTICS: Track MODAL_OPENED
      if (profile) {
        trackEvent(ANALYTICS_EVENTS.MODAL_OPENED, {
          modal_id: 'people-profile',
          modal_type: 'profile',
          person_id: profile.id,
          person_name: profile.name,
          person_title: profile.title,
        });
      }

      // ⚠️ ANALYTICS: Track MODAL_CONTENT_VIEWED after 2 seconds of engagement
      contentViewedTimeoutRef.current = setTimeout(() => {
        if (!contentViewedRef.current && profile) {
          contentViewedRef.current = true;
          trackEvent(ANALYTICS_EVENTS.MODAL_CONTENT_VIEWED, {
            modal_id: 'people-profile',
            person_id: profile.id,
            person_name: profile.name,
            time_to_viewed_ms: 2000,
          });
        }
      }, 2000);

      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      });
    } else {
      if (contentViewedTimeoutRef.current) {
        clearTimeout(contentViewedTimeoutRef.current);
        contentViewedTimeoutRef.current = null;
      }
    }
  }, [open, profile]);

  // ⚠️ ANALYTICS: Handle modal close with tracking
  const handleClose = useCallback((closeMethod: 'button' | 'backdrop' | 'escape') => {
    if (profile) {
      trackEvent(ANALYTICS_EVENTS.MODAL_CLOSED, {
        modal_id: 'people-profile',
        modal_type: 'profile',
        person_id: profile.id,
        person_name: profile.name,
        close_method: closeMethod,
        time_in_modal_ms: Date.now() - modalOpenTime.current,
      });
    }
    onOpenChange(false);
  }, [profile, onOpenChange]);

  // ⚠️ ANALYTICS: Handle LinkedIn link click
  const handleLinkedInClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (profile?.linkedin_url) {
      trackEvent(ANALYTICS_EVENTS.LINK_CLICKED, {
        link_type: 'external',
        link_text: 'LinkedIn',
        link_url: profile.linkedin_url,
        context: 'people_profile_modal',
        person_name: profile.name,
      });
    }
  }, [profile]);

  const handleMobileScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    const MOBILE_IMAGE_HEIGHT_PX = 320;
    const CLOSE_TOP_PX = 16;
    const CLOSE_SIZE_PX = 45;

    const threshold = MOBILE_IMAGE_HEIGHT_PX - (CLOSE_TOP_PX + CLOSE_SIZE_PX);
    setIsMobileCloseOnDark(scrollTop < threshold);

    const NAME_START_PX = MOBILE_IMAGE_HEIGHT_PX + 24;
    const HEADER_BAR_BOTTOM = CLOSE_TOP_PX + CLOSE_SIZE_PX + 16;
    const nameScrolledOut = scrollTop > NAME_START_PX - HEADER_BAR_BOTTOM + 60;
    setShowFloatingAuthor(nameScrolledOut);
  }, []);

  if (!profile) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        handleClose('backdrop');
      }
    }}>
      {/* Full-screen takeover on all breakpoints */}
      <DialogContent
        motion="fade"
        overlayMotion="fade"
        className={cn(
          "border-none [&>button]:hidden bg-white overflow-hidden",
          "fixed inset-0 w-full h-full max-w-none translate-x-0 translate-y-0 left-0 top-0 rounded-none p-0"
        )}
      >
        <VisuallyHidden>
          <DialogTitle>{profile.name}</DialogTitle>
        </VisuallyHidden>

        {/* ========== MOBILE ========== */}
        <div className="relative flex flex-col flex-1 min-h-0 sm:hidden h-full">
          <div
            ref={scrollContainerRef}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]"
            onScroll={handleMobileScroll}
          >
            {/* Pinned header bar */}
            <div className="sticky top-0 z-30 h-0 pointer-events-none">
              <button
                onClick={() => handleClose('button')}
                className={cn(
                  "absolute top-4 right-4 flex h-[45px] w-[45px] items-center justify-center rounded-full bg-transparent transition-colors duration-200 hover:opacity-70 pointer-events-auto",
                  isMobileCloseOnDark ? "text-platinum" : "text-carbon"
                )}
              >
                <XCircle size={45} weight="fill" />
              </button>

              <div
                className={cn(
                  "absolute top-[20.5px] left-4 right-[75px] transition-all duration-200 pointer-events-none",
                  showFloatingAuthor ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
                )}
              >
                  <div className="inline-flex items-center gap-2 bg-carbon rounded-full h-[36px] pl-[3px] pr-4 max-w-full">
                    <div className="w-[30px] h-[30px] rounded-full overflow-hidden bg-graphite flex-shrink-0">
                      {profile.image_url ? (
                        <img
                          src={optimizeImage(profile.image_url, { width: 60 })}
                          alt={profile.name}
                          loading="eager"
                          decoding="async"
                          width={30}
                          height={30}
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>
                    <span className="text-[13px] font-semibold text-platinum leading-none truncate">
                      {profile.name}
                    </span>
                  </div>
              </div>
            </div>

            {/* Image */}
            <div className="relative h-[320px] bg-graphite">
              {profile.image_url ? (
                <img
                  src={optimizeImage(profile.image_url, { width: 750 })}
                  alt={profile.name}
                  loading="eager"
                  decoding="async"
                  width={750}
                  height={320}
                  className="block w-full h-full object-cover object-top"
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <User className="h-24 w-24 text-muted-foreground/30" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="bg-white px-6 pb-8 pt-6">
              <h2 className="text-[36px] font-semibold text-carbon leading-tight tracking-tight">
                {profile.name}
              </h2>
              <DialogDescription className="text-[20px] font-bold text-coal mt-1 leading-tight">
                {profile.title}
              </DialogDescription>

              {/* Years + Specialisations */}
              {(profile.years_experience && profile.years_experience > 0) && (
                <>
                  <div className="my-6 border-t border-grey/50" />
                  <div>
                    <span className="text-17 font-medium text-carbon block mb-3">
                      {profile.years_experience} years of experience in:
                    </span>
                    {profile.specialisations && profile.specialisations.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {profile.specialisations.map(s => (
                          <span key={s.id} className="text-[11px] font-medium text-coal bg-grey rounded-full px-3 py-1.5">
                            {s.specialisation}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Specialisations without years */}
              {(!profile.years_experience || profile.years_experience <= 0) && profile.specialisations && profile.specialisations.length > 0 && (
                <>
                  <div className="my-6 border-t border-grey/50" />
                  <SpecialisationsSection items={profile.specialisations} />
                </>
              )}

              {/* Profile */}
              <div className="my-6 border-t border-grey/50" />
              <div>
                <span className="text-17 font-medium text-carbon block mb-3">Profile</span>
                <p className="text-[14px] font-normal text-carbon leading-relaxed">{profile.bio}</p>
              </div>

              {/* Experience */}
              {profile.experiences && profile.experiences.length > 0 && (
                <>
                  <div className="my-6 border-t border-grey/50" />
                  <div>
                    <span className="text-17 font-medium text-carbon block mb-3">Experience</span>
                    <div className="flex flex-col gap-2">
                      {profile.experiences.map((exp, i) => (
                        <p key={exp.id} className="text-[12px] font-normal text-carbon leading-tight">
                          <span className="font-bold">{String(i + 1).padStart(2, '0')}.</span> {exp.experience}
                        </p>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Credentials */}
              {profile.credentials && profile.credentials.length > 0 && (
                <>
                  <div className="my-6 border-t border-grey/50" />
                  <div>
                    <span className="text-17 font-medium text-carbon block mb-3">Credentials</span>
                    <div className="flex flex-col gap-2">
                      {profile.credentials.map((c, i) => (
                        <p key={c.id} className="text-[12px] font-normal text-carbon leading-tight">
                          <span className="font-bold">{String(i + 1).padStart(2, '0')}.</span> {c.credential}
                        </p>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Education */}
              {profile.education && profile.education.length > 0 && (
                <>
                  <div className="my-6 border-t border-grey/50" />
                  <div>
                    <span className="text-17 font-medium text-carbon block mb-3">Education</span>
                    <div className="flex flex-col gap-2">
                      {profile.education.map((e, i) => (
                        <p key={e.id} className="text-[12px] font-normal text-carbon leading-tight">
                          <span className="font-bold">{String(i + 1).padStart(2, '0')}.</span> {e.education}
                        </p>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Connect */}
              {profile.linkedin_url && (
                <>
                  <div className="my-6 border-t border-grey/50" />
                  <div>
                    <span className="text-17 font-medium text-carbon block mb-3">Connect</span>
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-10 h-10 rounded-xl transition-opacity duration-200 hover:opacity-70"
                      onClick={handleLinkedInClick}
                    >
                      <img src={linkedinIconSquare} alt="LinkedIn" loading="eager" decoding="async" width={32} height={32} className="w-8 h-8 text-carbon" />
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ========== DESKTOP: 3-column grid ========== */}
        <div className="hidden sm:grid sm:grid-cols-3 h-full">
          {/* Column 1: Full-height image */}
          <div className="relative h-full overflow-hidden bg-graphite">
            {profile.image_url ? (
              <img
                src={optimizeImage(profile.image_url, { width: 900 })}
                alt={profile.name}
                loading="eager"
                decoding="async"
                width={900}
                height={600}
                className="block w-full h-full object-cover object-top"
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <User className="h-24 w-24 text-muted-foreground/30" />
              </div>
            )}
            {/* LinkedIn moved to content side */}
          </div>

          {/* Columns 2-3: Content area */}
          <div className="col-span-2 h-full flex flex-col bg-white min-w-0">
            {/* Top: Name & Title with generous breathing room */}
            <div className="flex-1 flex flex-col justify-center px-12 xl:px-16 min-h-0">
              <span className="text-[24px] font-bold text-coal leading-[28px] tracking-[0.216px] mb-4">
                {profile.title}
              </span>
              <h2 className="text-[96px] xl:text-[112px] font-semibold text-carbon leading-[1] tracking-tight">
                {profile.name}
              </h2>
              {(profile.years_experience && profile.years_experience > 0 || (profile.specialisations && profile.specialisations.length > 0)) && (
                <div className="mt-6 flex items-center gap-5">
                  {profile.years_experience && profile.years_experience > 0 && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-40 font-semibold text-carbon leading-none">{profile.years_experience}</span>
                      <span className="text-14 font-medium text-carbon leading-tight">years<br />experience{profile.specialisations && profile.specialisations.length > 0 ? ' in' : ''}</span>
                    </div>
                  )}
                  {profile.specialisations && profile.specialisations.length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center pt-1">
                      {profile.specialisations.map(s => (
                        <span key={s.id} className="text-[11px] font-medium text-coal bg-grey rounded-full px-3 py-1.5">
                          {s.specialisation}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Bottom: 4-column detail grid */}
            <div className="px-12 xl:px-16 pb-10">
              {profile.linkedin_url && (
                <div className="mb-4">
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center transition-opacity duration-200 hover:opacity-70"
                    onClick={handleLinkedInClick}
                  >
                    <img src={linkedinIconSquare} alt="LinkedIn" loading="eager" decoding="async" width={20} height={20} className="w-5 h-5" />
                  </a>
                </div>
              )}
              <div className="border-t border-grey/30 pt-8" />
              <div className="grid grid-cols-3 gap-x-10">
                {(() => {
                  // Build sections dynamically
                  const sections: React.ReactNode[] = [];

                  // Profile/Bio is always first
                  sections.push(
                    <div key="profile" className="min-w-0">
                      <span className="text-[13px] font-bold text-carbon block mb-2">Profile</span>
                      <p className="text-[12px] font-normal text-carbon leading-relaxed break-words whitespace-pre-line">{profile.bio}</p>
                    </div>
                  );

                  // Experience
                  if (profile.experiences && profile.experiences.length > 0) {
                    sections.push(
                      <div key="experience" className="min-w-0">
                        <span className="text-[13px] font-bold text-carbon block mb-2">Experience</span>
                        <div className="flex flex-col gap-1">
                          {profile.experiences.map((exp, i) => (
                            <p key={exp.id} className="text-[10px] font-normal text-carbon leading-snug break-words">
                              <span className="font-bold">{String(i + 1).padStart(2, '0')}.</span> {exp.experience}
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // Education
                  if (profile.education && profile.education.length > 0) {
                    sections.push(
                      <div key="education" className="min-w-0">
                        <span className="text-[13px] font-bold text-carbon block mb-2">Education</span>
                        <div className="flex flex-col gap-1">
                          {profile.education.map((e, i) => (
                            <p key={e.id} className="text-[10px] font-normal text-carbon leading-snug break-words">
                              <span className="font-bold">{String(i + 1).padStart(2, '0')}.</span> {e.education}
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // Credentials
                  if (profile.credentials && profile.credentials.length > 0) {
                    sections.push(
                      <div key="credentials" className="min-w-0">
                        <span className="text-[13px] font-bold text-carbon block mb-2">Credentials</span>
                        <div className="flex flex-col gap-1">
                          {profile.credentials.map((c, i) => (
                            <p key={c.id} className="text-[10px] font-normal text-carbon leading-snug break-words">
                              <span className="font-bold">{String(i + 1).padStart(2, '0')}.</span> {c.credential}
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // If 4+ sections, stack last two in column 3
                  if (sections.length >= 4) {
                    const first = sections[0];
                    const second = sections[1];
                    const rest = sections.slice(2);
                    return (
                      <>
                        {first}
                        {second}
                        <div className="min-w-0 flex flex-col gap-5">{rest}</div>
                      </>
                    );
                  }

                  // 3 or fewer: one section per column
                  return sections;
                })()}
              </div>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={() => handleClose('button')}
            className="absolute top-5 right-5 flex h-[45px] w-[45px] items-center justify-center rounded-full bg-transparent text-graphite transition-all duration-300 hover:opacity-70 z-10"
          >
            <XCircle size={45} weight="fill" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PeopleProfileDialog;
